import { faPauseCircle, faPlayCircle } from "@fortawesome/pro-solid-svg-icons"
import { IAgency } from "agency/model"
import { getAgencyById, getAgencyNameById } from "agency/tools/common"
import { DEFAULT_LOCALE } from "app/language/model"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { getStore } from "app/store/store"
import { initializeAPICredentials, resetAPICredentials } from "app/tools/api"
import { wait } from "app/tools/common"
import { getDifferenceInMilliSeconds } from "app/util/date/tools/common"
import { localStorageSVC } from "app/util/local-storage/service"
import { CURRENCIES } from "currency/model"
import { messageSVC } from "message/service"
import { HOME_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import { userEventModule } from "user/event"
import {
  EMAIL_REGEX,
  EMPTY_TOKEN_VALUE,
  FAHRENHEIGHT_CURRENCIES,
  INVALID_CREDENTIALS,
  IPasswordChange,
  IUser,
  IUserCreationResult,
  IUserFormData,
  IUserImpersonateSession,
  IUserInfo,
  IUserShort,
  LOCAL_STORAGE_REMEMBER_ME,
  LOCAL_STORAGE_TOKEN,
  USER_ALREADY_EXISTS_ERROR,
  USER_EVENTS,
  USER_ROLES
} from "user/model"
import { userRPO } from "user/repository"
import { state } from "user/state"
import { userActionCreator } from "user/store/actions"
import { isSspUserSession } from "./permissions"
import { RENEW_SESSION_CHECK_TIME_IN_MS, checkRenewSession } from "./relogin"

export function getUser(rootState?: IRootState): IUser {
  return (rootState || getStore().getState()).user.user
}

export function getUserId(userInfo?: IUserInfo): number {
  const { user, impersonate } = !userInfo ? getStore().getState().user : userInfo
  return impersonate ? impersonate.user_id : user.id
}

export const getUserAgencyId = (): number | null => getStore().getState().user.user.agency_id

export function getUserInfo(): IUserInfo {
  return getStore().getState().user
}

export function getUsername(): string {
  return getStore().getState().user.user.lastname || getStore().getState().user.user.email || ""
}

export function getToken(): string {
  return getStore().getState().user.token
}

export function getUserCurrencyId(): string {
  return getStore().getState().user.user.currency_id
}

export function isCelsiusTemperatureUnit(): boolean {
  return !FAHRENHEIGHT_CURRENCIES.includes(getStore().getState().user.user.currency_id)
}

export function login(email: string, clearPassword: string, rememberMe: boolean = false): Promise<IUserInfo> {
  return new Promise<IUserInfo>((resolve, reject) => {
    getStore().dispatch(userActionCreator.startLoggingIn())
    userRPO
      .login(email, clearPassword)
      .then(userInfo => {
        if (rememberMe) {
          localStorageSVC.set(LOCAL_STORAGE_TOKEN, userInfo.token)
        } else {
          localStorageSVC.remove(LOCAL_STORAGE_TOKEN)
        }
        setUserInfo(userInfo)
        userEventModule.notify(USER_EVENTS.LOGIN, { userInfo })
        resolve(userInfo)
      })
      .catch((error: any) => {
        setUserInfo()
        reject(error)
      })
  })
}

export function isLoggedIn(rootState?: IRootState): boolean {
  return (rootState || getStore().getState()).user.token !== EMPTY_TOKEN_VALUE
}

export async function logoutAndRedirectToHomePage(ignoreConfirmation?: boolean): Promise<void> {
  if (await logout(ignoreConfirmation)) {
    // means the user didn't cancel the logout
    navigate(HOME_PATH)
  }
}

export async function logout(ignoreConfirmation?: boolean): Promise<boolean> {
  return new Promise<boolean>(async resolve => {
    if (ignoreConfirmation) {
      await processLogout()
      resolve(true)
      return true
    }
    messageSVC.confirmMessage({
      title: translate("user.logout.title"),
      message: translate("user.logout.message"),
      addGenericConfirmMessage: true,
      onSuccess: async () => {
        await processLogout()
        resolve(true)
      },
      onCancel: () => {
        resolve(false)
      }
    })
  })
}

export const logoutAndLoginAgain = async () => {
  const token = getToken()
  setUserInfo()
  userEventModule.notify(USER_EVENTS.LOGOUT, {})
  await loginWithToken(token)
}

export const getUserName = (user: IUserShort): string => {
  if (!user) {
    return "-"
  }
  const username = `${user.lastname || ""} ${user.firstname || ""}`.trim()
  return username || user.email || "-"
}

// always resolve
async function processLogout(): Promise<void> {
  try {
    while (getStore().getState().app.isInitializating) {
      // else previous init is not finished and can cause server request errors in tests
      await wait(100)
    }
    await userRPO.logout()
  } catch (error) {
    resetAPICredentials()
    console.warn("Server logout failed", error)
  }
  clearTimeout(state.sessionKeeperTimeout)
  localStorageSVC.remove(LOCAL_STORAGE_TOKEN)
  setUserInfo()
  userEventModule.notify(USER_EVENTS.LOGOUT, {})
}

export function getUserById(userId: number): Promise<IUser> {
  return userRPO.getUserById(userId, false)
}

// set user's info
export function setUserInfo(userInfo?: IUserInfo, impersonateSession?: IUserImpersonateSession): void {
  try {
    clearTimeout(state.sessionKeeperTimeout)
    if (userInfo && userInfo.token !== EMPTY_TOKEN_VALUE) {
      initializeAPICredentials(userInfo.token)
      getStore().dispatch(userActionCreator.login(userInfo, impersonateSession))
      keepTokenValid(userInfo)
    } else {
      resetAPICredentials()
      getStore().dispatch(userActionCreator.logout())
    }
  } catch (ex) {
    console.error("Unexpected error while initializing the project data", ex)
    getStore().dispatch(userActionCreator.stopLoggingIn())
  }
}

export async function checkTokenInLocalStorage(): Promise<void> {
  const localStorageToken = localStorageSVC.get(LOCAL_STORAGE_TOKEN)
  if (!localStorageToken) {
    return
  }
  await loginWithToken(localStorageToken)
}

const loginWithToken = async (token: string) => {
  try {
    const userInfo = await userRPO.getUserInfoFromToken(token)
    if (!userInfo) {
      return
    }
    setUserInfo(userInfo)
    userEventModule.notify(USER_EVENTS.LOGIN, { userInfo })
  } catch (ex) {
    console.error(ex)
  }
}

export function isEmailValid(email: string) {
  return typeof email === "string" && email !== "" && EMAIL_REGEX.test(email)
}

export function getUserDisplayName(user?: IUserShort | IUserInfo): string {
  let userShort: IUserShort
  let userInfo: IUserInfo | undefined
  if (!user) {
    userInfo = getStore().getState().user
  } else if ((user as any).token) {
    userInfo = user as IUserInfo
  }
  if (userInfo) {
    if (userInfo.impersonate) {
      userShort = {
        ...userInfo.impersonate,
        id: userInfo.impersonate.user_id
      }
    } else {
      userShort = userInfo.user
    }
  } else {
    userShort = user as IUserShort
  }
  const firstname = userShort.firstname
  const lastname = userShort.lastname
  if (firstname && lastname) {
    return `${firstname} ${lastname}`
  } else if (!firstname && lastname) {
    return lastname
  } else if (!lastname && firstname) {
    return firstname
  }
  return userShort.email || ""
}

export function getUserEmail(): string {
  return getStore().getState().user.user.email || ""
}

export const getUserAgency = (): IAgency => getAgencyById(getStore().getState().user.user.agency_id)

export function getUserAgencyOrEmail(): string {
  const agencyId = getStore().getState().user.user.agency_id || -1
  return getAgencyNameById(agencyId) || getStore().getState().user.user.email || ""
}

export function getSavedRememberMe(): boolean {
  const rememberMeStr = localStorageSVC.get(LOCAL_STORAGE_REMEMBER_ME)
  if (!rememberMeStr) {
    return false
  }
  return rememberMeStr === "true"
}

export function saveRememberMe(rememberMe: boolean): void {
  localStorageSVC.set(LOCAL_STORAGE_REMEMBER_ME, JSON.stringify(rememberMe))
}

export function checkIfSessionIsOrWillExpired(): void {
  checkRenewSession(getUserInfo())
}

export function getSessionExpirationTimeInMilliSeconds(userInfo?: IUserInfo): number {
  if (!userInfo) {
    userInfo = getUserInfo()
  }
  return getDifferenceInMilliSeconds(new Date(), userInfo.tokenExpirationDateTime)
}

export function keepTokenValid(userInfo: IUserInfo): void {
  state.sessionKeeperTimeout = setTimeout(
    () => checkRenewSession(userInfo),
    getSessionExpirationTimeInMilliSeconds(userInfo) - RENEW_SESSION_CHECK_TIME_IN_MS
  )
}

export const createUser = async (email: string, data: Partial<IUser>): Promise<IUserCreationResult | "error"> => {
  const clearPassword = Math.random().toString(36).slice(-8)
  return userRPO
    .createUser(email, data, clearPassword)
    .then(updatedUser => ({ user: updatedUser, clearPassword }))
    .catch(error => {
      console.error(error)
      messageSVC.error(translate(error === USER_ALREADY_EXISTS_ERROR ? "user.email.already.exist" : "user.add.error"))
      return "error"
    })
}

export const setUserActivation = (userId: number, userName: string, activate: boolean): Promise<IUser | null> =>
  new Promise<IUser | null>(resolve => {
    messageSVC.confirmMessage({
      title: translate(activate ? "user.activate.title" : "user.deactivate.title"),
      message: translate(activate ? "user.activate.message" : "user.deactivate.message", userName),
      icon: activate ? faPlayCircle : faPauseCircle,
      onSuccess: () => {
        userRPO
          .updateUser(userId, { active: activate })
          .then(user => resolve(user))
          .catch(error => {
            console.error(error)
            messageSVC.error(translate(activate ? "user.activate.error" : "user.deactivate.error"))
            resolve(null)
          })
      },
      onCancel: () => resolve(null)
    })
  })

export const updateUser = (
  data: Partial<IUser>,
  userId: number,
  passwordChange?: IPasswordChange
): Promise<IUser | void | "error"> =>
  new Promise<IUser | void | "error">(resolve => {
    if (typeof userId !== "number") {
      console.error("Missing user id")
      return resolve("error")
    }
    userRPO
      .updateUser(userId, data, passwordChange)
      .then(updatedUser => {
        if (updatedUser.id === getUserId()) {
          // updated user is currently loggued in user
          setUserInfo({ ...getStore().getState().user, user: updatedUser })
          const currentUser = getUser()
          if (currentUser.ui_settings.distance_unit !== updatedUser.ui_settings.distance_unit) {
            userEventModule.notify(USER_EVENTS.DISTANCE_UNIT_CHANGE, {
              oldDistanceUnit: currentUser.ui_settings.distance_unit,
              newDistanceUnit: updatedUser.ui_settings.distance_unit
            })
          }
        }
        resolve(updatedUser)
      })
      .catch(error => {
        console.error(error)
        messageSVC.error(
          translate(
            error === USER_ALREADY_EXISTS_ERROR
              ? "user.email.already.exist"
              : error === INVALID_CREDENTIALS
                ? "user.login.error.current.password"
                : "user.update.error"
          )
        )
        resolve("error")
      })
  })

export const updateCurrentUser = (data: Partial<IUser>): Promise<IUser | void | "error"> =>
  new Promise<IUser | void | "error">(resolve => {
    return updateUser(data, getUserId())
  })

export const getDefaultUser = (agencyId: number, currencyId: CURRENCIES): IUserFormData => ({
  email: "",
  agency_id: agencyId,
  active: true,
  currency_id: currencyId,
  language_id: DEFAULT_LOCALE,
  role: isSspUserSession() ? USER_ROLES.SSP_USER : USER_ROLES.DSP_USER
})

const passwordRegex = new RegExp(/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!#$%&@?"']).{8,}/)
export const isValidPassword = (password: string): boolean => passwordRegex.test(password)

const emailRegex = new RegExp(
  /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
)
export const isValidEmail = (email: string): boolean => emailRegex.test(email)
